/**
 * Shared icon + color tables for the Leaflet map.
 *
 * Extracted out of MapPage so panel components (and any future map
 * widgets) can render category swatches without pulling the whole page in.
 */
import L from "leaflet";

export const CATEGORY_COLORS = {
  fast_travel: "#00E5FF",
  fast_travel_tree: "#7DD3FC",  // World Tree region (2026-07 update)
  dungeon: "#9D4CDD",
  alpha_pal: "#FFD700",
  predator_pal: "#FF3D8B",
  bounty: "#FF6B35",
  pal_spawn: "#7FE3C4",
  lifmunk_effigy: "#2A9D8F",
  sealed_realm: "#E63946",
  boss_tower: "#FF6B35",
  treasure: "#FFD700",
  oil_rig: "#A9A2B5",
  npc: "#A9A2B5",
  merchant: "#9D4CDD",
  resource: "#F4A261",
  note: "#FFE066",
  anti_air_raid: "#D94F37",
  // 1.0 categories sourced from paldeck.cc (2026-09). Colours chosen to stay
  // distinguishable from the palette above at small marker sizes.
  fishing_spot: "#5B8DEF",
  ancient_ruins: "#C2A878",
  supply_drop: "#7CB342",
  enemy_camp: "#B5352B",
  watchtower: "#8E7CC3",
  respawn_point: "#A3E635",
  custom: "#F9F6EE",
};

// Categories whose rows carry a meaningful sub-type in their metadata. The
// sidebar renders these as a collapsible chip list so 8,345 resource nodes
// can be narrowed to "just coal", without bloating the category list itself.
export const CATEGORY_SUBTYPES = {
  resource: { metaKey: "resource", label: "Node type" },
  fishing_spot: { metaKey: "fish", label: "Tier" },
  lifmunk_effigy: { metaKey: "relic", label: "Effigy type" },
};

// Category rows whose DB key reads badly in the sidebar. `lifmunk_effigy`
// actually holds 12 relic types, only 155 of which are Lifmunk.
export const CATEGORY_LABELS = {
  lifmunk_effigy: "Effigy",
};

// paldeck keys relics by the Pal's internal asset name — show the Pal name
// players actually recognise instead.
export const SUBTYPE_LABELS = {
  lifmunk_effigy: {
    lifmunk: "Lifmunk",
    sheepball: "Lamball",
    penguin: "Pengullet",
    icecrocodile: "Munchill",
    flamebambi: "Rooby",
    leafmomonga: "Herbil",
    monkey: "Tanzee",
    negativekoala: "Depresso",
    pinkcat: "Cattiva",
    mutant: "Lunaris",
    lazydragon: "Relaxaurus",
    guardiandog: "Yakumo",
  },
};

// Per-subtype pin colours. Only defined where the distinction is worth
// seeing at a glance on the map — fishing tiers. Resource nodes keep the
// single category colour (they already differ by item sprite).
export const SUBTYPE_COLORS = {
  fishing_spot: {
    common: "#5B8DEF",
    rare: "#C084FC",
    dungeon: "#F97316",
    mini: "#34D399",
  },
};

/** The sub-type value for a marker, or "" when its category has none. */
export function subtypeOf(marker) {
  const cfg = CATEGORY_SUBTYPES[marker?.category];
  if (!cfg) return "";
  return String(marker?.metadata?.[cfg.metaKey] ?? "");
}

/** Pin colour for a marker, honouring per-subtype overrides. */
export function markerColor(category, subtype) {
  return (
    SUBTYPE_COLORS[category]?.[subtype] ||
    CATEGORY_COLORS[category] ||
    "#F9F6EE"
  );
}

/** "paldium_ore" → "Paldium Ore"; uses the curated label when there is one. */
export function prettySubtype(value, category) {
  const mapped = SUBTYPE_LABELS[category]?.[value];
  if (mapped) return mapped;
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Display name for a category row. */
export function prettyCategory(category) {
  return (
    CATEGORY_LABELS[category] ||
    String(category || "").replace(/_/g, " ")
  );
}

export const CATEGORY_GLYPH = {
  fast_travel: "▲", fast_travel_tree: "▲", dungeon: "◆", alpha_pal: "★", predator_pal: "✘",
  bounty: "⚑", pal_spawn: "·",
  lifmunk_effigy: "✦", sealed_realm: "◉", boss_tower: "▼", treasure: "♦",
  oil_rig: "▣", npc: "●", merchant: "$",
  resource: "⬡", note: "✎", anti_air_raid: "◎",
  fishing_spot: "≈", ancient_ruins: "⌂", supply_drop: "▽",
  enemy_camp: "⚔", watchtower: "♜", respawn_point: "⟲",
  custom: "•",
};

// Real game-icon sprites for the categories where a recognizable one exists
// (extracted game art via PalworldSaveTools, served from
// backend/assets/map/marker_icons/). Categories NOT listed here keep their
// glyph pins on purpose — the game has no distinct small icon for them
// (dungeon entrances, sealed realms, faction towers, bounties, notes), and a
// wrong sprite is worse than a clean glyph. makeIcon() renders sprites inside
// the same tinted ring, so CATEGORY_COLORS still color-codes everything.
export const CATEGORY_SPRITE = {
  fast_travel: "fast_travel.webp",           // eagle statue (build icon)
  fast_travel_tree: "fast_travel_tree.webp", // same statue, tree-region tint
  alpha_pal: "alpha_pal.webp",               // PST's in-game alpha marker
  predator_pal: "predator_pal.webp",         // alpha marker + pink ring
  lifmunk_effigy: "lifmunk_effigy.webp",     // the Relic item itself
  treasure: "treasure.webp",                 // treasure chest
  oil_rig: "oil_rig.webp",                   // oil derrick
  merchant: "merchant.webp",                 // wandering trader portrait
  resource: "resource.webp",                 // stone/ore node
};

/** Absolute URL for a category's sprite, or null when the category
 * intentionally uses its glyph pin. `assetsBase` is the caller's ASSETS
 * constant (markerIcons stays free of api.js imports so widgets can reuse
 * it with their own base). */
export function categorySpriteUrl(assetsBase, category) {
  const f = CATEGORY_SPRITE[category];
  return f ? `${assetsBase}/map/marker_icons/${f}` : null;
}

// One representative icon per category for the sidebar list. Taken from the
// art the pins themselves use, but pinned down explicitly: categories with
// per-row art (alpha portraits, 12 effigy types, 17 resource nodes) would
// otherwise show whichever marker happened to load first.
const CATEGORY_LIST_ICON = {
  fast_travel: "compass/T_icon_compass_FTtower.png",
  fast_travel_tree: "compass/T_icon_compass_FTtower.png",
  watchtower: "compass/T_icon_compass_FTtower.png",
  boss_tower: "compass/T_icon_compass_tower.png",
  dungeon: "compass/T_icon_compass_dungeon.png",
  treasure: "chest-loc.png",
  note: "note-loc.png",
  enemy_camp: "camp-loc.png",
  sealed_realm: "sealed_realm.png",
  anti_air_raid: "anti_air_raid.png",
  ancient_ruins: "ancient_ruins.png",
  supply_drop: "supply_drop.png",
  fishing_spot: "fishing_spot.png",
  respawn_point: "respawn_point.png",
  npc: "npcs/T_CommonHuman_icon_normal.webp",
  bounty: "npcs/T_BOSS_NPC_Female_Soldier03.webp",
  resource: "items/T_itemicon_Material_Stone.png",
  pal_spawn: "egg-loc.png",
};

/** Icon for a sidebar category row — the same art its pins use on the map. */
export function categoryListIcon(assetsBase, category) {
  const f = CATEGORY_LIST_ICON[category];
  if (f) return `${assetsBase}/map_icons/${f}`;
  return categorySpriteUrl(assetsBase, category);
}

export const TILE_SIZE = 256;
export const NATIVE = 8192;
// At zoom 0 the world is one TILE_SIZE tile. Scale image-space (0-8192) to zoom-0 coords (0-256).
export const SCALE = TILE_SIZE / NATIVE; // 1/32

// Palworld coordinate constants (2026-07 update) — mirror the backend
// `live.py` game_to_tile / tile_to_game pipeline so the cursor readout
// on the world map can inverse-map a pixel back to raw UE sav-coords.
//
// Source: palworld_coord library __transl_x_new / __transl_y_new / __scale_new.
const TRANSL_X = 375247;
const TRANSL_Y = -18;
const COORD_SCALE = 725;
const COORD_RANGE = 1000;   // main map paldex extends ±1000

/** Convert tile-space (x, y) → Palworld in-game M-map coords (the small
 * numbers shown when you press M / that PalDefender's /spawnpal expects).
 *
 * Pipeline (matches backend live.tile_to_game inverse, then applies the
 * canonical `palworld-coord` OLD formula to reach in-game M-map space):
 *   1. tile-space (0-8192, Y inverted) → paldex ±1000
 *   2. paldex → raw UE sav-coords (inverse of sav_to_map(new=True))
 *   3. sav-coords → in-game M-map via the OLD projection, which is
 *      what /spawnpal and the in-game teleport commands still consume
 *      even after the 2026-07 update (verified in `raids.py` where all
 *      `sav_to_map(...)` calls pass `new=False`).
 *
 * Users can therefore hover any point on the map and paste the readout
 * directly into PalDefender's spawn command or an in-game teleport.
 */
export function tileToGame(tileX, tileY) {
  // Step 1: tile-space → paldex (new projection)
  const scalePx = NATIVE / (2 * COORD_RANGE);   // 4.096
  const paldexX = tileX / scalePx - COORD_RANGE;
  const paldexY = COORD_RANGE - tileY / scalePx;
  // Step 2: paldex → raw sav (inverse of the sav_to_map new pipeline)
  const new_y = paldexX * COORD_SCALE;
  const new_x = paldexY * COORD_SCALE;
  const sav_x = new_x - TRANSL_X;
  const sav_y = new_y + TRANSL_Y;
  // Step 3: sav → in-game M-map via the OLD library formula (still what
  // /spawnpal + in-game teleport use, so users can cross-reference).
  const map_x = Math.round((sav_y - 158000) / 459);
  const map_y = Math.round((sav_x + 123888) / 459);
  return { x: map_x, y: map_y };
}

/** Build a Leaflet divIcon for a marker. If `iconUrl` is provided the
 * sprite is rendered inside a tinted ring; otherwise the legacy glyph pin
 * is used as a fallback. */
// HTML-attribute escaping for user/server-derived strings that get
// glued into the `innerHTML` we hand to Leaflet's divIcon. Without it
// a future raid sprite URL containing a stray `"` could break out and
// run inline JS (classic stored-XSS). Keep this in lock-step with
// any new attribute interpolation below.
const escAttr = (s = "") => String(s)
  .replace(/&/g, "&amp;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;");

export function makeIcon(category, collected, iconUrl, opts = {}) {
  const color = opts.color || CATEGORY_COLORS[category] || "#F9F6EE";
  const glyph = CATEGORY_GLYPH[category] || "•";
  // Small corner chip — used for alpha-pal levels so a player can spot a
  // Lv 78 alpha without opening the popup.
  const badge = opts.badge
    ? `<span class="marker-pin-badge" style="border-color:${escAttr(color)}">${escAttr(opts.badge)}</span>`
    : "";
  if (iconUrl) {
    const safeUrl = escAttr(iconUrl);
    const safeColor = escAttr(color);
    const safeGlyph = escAttr(glyph);
    // Keep the fallback in CSS-classes / data-attrs (not inline JS) so we
    // don't have to escape a JS string. A tiny <script>-free CSS pin is
    // enough — the failed <img> is hidden via `onerror` swapping a class.
    return L.divIcon({
      className: "leaflet-marker-skull",
      html:
        `<div class="marker-pin marker-pin-img ${collected ? "collected" : ""}"` +
        ` style="border-color:${safeColor};box-shadow:0 0 8px ${safeColor}99;"` +
        ` data-fallback-color="${safeColor}" data-fallback-glyph="${safeGlyph}">` +
        `<img src="${safeUrl}" loading="lazy" alt=""` +
        ` onerror="(function(i){var p=i.parentNode;i.remove();p.classList.add('marker-pin-fallback');p.textContent=p.dataset.fallbackGlyph;p.style.background=p.dataset.fallbackColor;p.style.color='#06030A';})(this)" />` +
        badge +
        `</div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
    });
  }
  return L.divIcon({
    className: "leaflet-marker-skull",
    html: `<div class="marker-pin ${collected ? "collected" : ""}" style="background:${escAttr(color)};color:#06030A;">${escAttr(glyph)}${badge}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
  });
}
